import { NgComponentOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { ButtonComponent } from '@shared/ui/button';
import { CopyTextComponent } from '@shared/ui/copy-text';
import { InfoComponent, InfoTypeEnum } from '@shared/ui/info';
import { ModalCloseDirective } from '@shared/ui/modals';
import { cloneDeep } from 'lodash-es';
import { NgScrollbar } from 'ngx-scrollbar';
import { finalize } from 'rxjs';
import { FormInputCheckboxComponent } from './checkbox/form-input-checkbox.component';
import {
  activeFormControls,
  FormActionInterface,
  FormButtonTypeEnum,
  FormControlInterface,
  FormControlTypeEnum,
  FormDataInterface,
} from './form.interface';
import { FormSelectComponent } from './select/form-select.component';
import { FormInputTextComponent } from './text/form-input-text.component';

type ExtendedFormControl = FormControlInterface & { id: string };

@Component({
  selector: 'shared-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.css'],
  imports: [
    MatDialogModule,
    ButtonComponent,
    ReactiveFormsModule,
    InfoComponent,
    CopyTextComponent,
    ModalCloseDirective,
    NgScrollbar,
    NgComponentOutlet,
    FormInputCheckboxComponent,
    FormSelectComponent,
    FormInputTextComponent,
  ],
})
export class FormComponent {
  dialogRef = inject<MatDialogRef<FormComponent>>(MatDialogRef);
  private readonly formBuilder = inject(FormBuilder);
  data: FormDataInterface = inject(MAT_DIALOG_DATA);

  @HostListener('window:keyup.Enter', ['$event'])
  onEnterClick(event: KeyboardEvent): void {
    this.runSubmitAction();
  }

  form = this.formBuilder.nonNullable.group(
    (this.data?.form?.ids ?? [])
      .filter((id) => {
        const controlType = this.data?.form?.controls?.[id]?.type;
        return controlType && activeFormControls.includes(controlType);
      })
      .reduce(
        (
          acc: { [key: string]: any },
          curr: string,
        ): { [key: string]: any } => ({
          ...acc,
          [curr]: [
            this.data?.form?.controls?.[curr]?.value,
            this.data?.form?.controls?.[curr]?.validation?.validators ?? [],
          ],
        }),
        {},
      ),
    { validators: this.data?.form?.validators ?? [] },
  );

  viewControls = signal<ExtendedFormControl[]>(this.getViewControls());

  actions =
    this.data?.actions?.map((action) =>
      !action.title
        ? {
            ...action,
            title:
              action.type === FormButtonTypeEnum.PRIMARY
                ? $localize`:@@formDefaultConfirmButton:Confirm`
                : $localize`:@@formDefaultCancelButton:Cancel`,
          }
        : action,
    ) ?? [];
  submitAction = this.actions.filter(
    (action) => action.type === FormButtonTypeEnum.PRIMARY,
  );

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  isSubmitting = signal(false);

  FormButtonTypeEnum = FormButtonTypeEnum;
  FormControlTypeEnum = FormControlTypeEnum;

  runSubmitAction(): void {
    this.runAction(this.submitAction[0]!, this.data.form?.controls);
  }

  runAction(
    action: FormActionInterface,
    controls?: Record<string, FormControlInterface>,
  ): void {
    if (action.action) {
      if (!this.isSubmitting() && this.form.valid) {
        this.isSubmitting.set(true);
        action
          .action(this.getFormValuesWithoutEdgeSpaces(), controls)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => this.isSubmitting.set(false)),
          )
          .subscribe((response: any) => {
            this.viewControls.set(this.getViewControls());

            if (
              !action.disableClose &&
              !(response && response['ignoreClose'])
            ) {
              this.dialogRef.close(true);
            }
          });
      }
    } else {
      if (!action.disableClose) {
        this.dialogRef.close(false);
      }
    }
  }

  private getViewControls(): ExtendedFormControl[] | [] {
    return this.data?.form?.ids
      ? this.data.form.ids.map((id: string) => ({
          ...this.data!.form!.controls[id]!,
          id: id,
        }))
      : [];
  }

  private getFormValuesWithoutEdgeSpaces(): { [key: string]: any } {
    const formValue: { [key: string]: any } = cloneDeep(this.form.value);

    Object.keys(formValue).forEach((key: string) => {
      const formControlValue = formValue[key];
      if (typeof formControlValue === 'string') {
        formValue[key] = formControlValue.trim();
      }
    });

    return formValue;
  }

  toControl(absCtrl: AbstractControl): FormControl {
    return absCtrl as FormControl;
  }

  protected readonly InfoTypeEnum = InfoTypeEnum;
}
